"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getCaseActiveTask,
  getCaseAgenticMission,
  type CaseAgenticMissionLink,
} from "@/services/api/case-checking";
import { useCaseCheckingTenantId } from "@/components/case-checking/caseTenantContext";
import { useTaskStream } from "@/hooks/useTaskStream";

import {
  agenticProgressLabel,
  caseCheckProgressDisplayMode,
  isAgenticMissionBlocked,
  shouldShowAgenticProgress,
  shouldShowMissionAttentionState,
  type AgenticCaseProgressCase,
  type CaseCheckProgressDisplayMode,
} from "./agenticCaseProgress";
import { buildCaseProgressLabel } from "./caseProgressLabel";
import { buildCaseProgressFallback } from "./missionStageProgress";
import { caseCheckingActiveTaskId } from "./caseCheckingWs";
import { isRunnerBackgroundTaskId } from "./runnerBackgroundTask";
import { useCaseCheckingRealtime } from "./useCaseCheckingRealtime";

const TERMINAL_MISSION_STATUSES = new Set(["completed", "closed", "cancelled", "failed"]);
const ACTIVE_TASK_POLL_MS = 5000;

function isMissionPollingComplete(link: CaseAgenticMissionLink | null): boolean {
  if (!link?.mission_id) return true;
  const status = (link.mission_status || "").toLowerCase();
  if (TERMINAL_MISSION_STATUSES.has(status)) return true;
  return link.is_active === false && !shouldShowMissionAttentionState(link);
}

function shouldPreserveProgressTaskId(
  current: string | null,
  next: CaseAgenticMissionLink | null,
): boolean {
  if (!current || !isRunnerBackgroundTaskId(current)) return false;
  if (next?.runner_task_id === current) return true;
  if (
    Boolean(next?.intake_failed || next?.attention_kind === "intake_failed") &&
    current === (next?.last_failed_runner_task_id || next?.runner_task_id)
  ) {
    return true;
  }
  return false;
}

function pickProgressTaskId(
  fromApi: string | null | undefined,
  missionLink: CaseAgenticMissionLink | null,
  initialTaskId?: string | null,
): string | null {
  if (isRunnerBackgroundTaskId(fromApi)) return fromApi!;
  if (isRunnerBackgroundTaskId(missionLink?.runner_task_id)) return missionLink!.runner_task_id!;
  if (isRunnerBackgroundTaskId(missionLink?.last_failed_runner_task_id)) return missionLink!.last_failed_runner_task_id!;
  if (isRunnerBackgroundTaskId(missionLink?.progress_task_id)) return missionLink!.progress_task_id!;
  if (isRunnerBackgroundTaskId(initialTaskId)) {
    const hasMissionRunner =
      isRunnerBackgroundTaskId(missionLink?.runner_task_id) ||
      isRunnerBackgroundTaskId(missionLink?.progress_task_id);
    if (!hasMissionRunner) return initialTaskId!;
  }
  return fromApi || missionLink?.progress_task_id || null;
}

function applyMissionLinkHint(
  missionLink: CaseAgenticMissionLink | null | undefined,
  initialTaskId?: string | null,
): { missionLink: CaseAgenticMissionLink | null; taskId: string | null } {
  const link = missionLink ?? null;
  return {
    missionLink: link,
    taskId: pickProgressTaskId(null, link, initialTaskId),
  };
}

export function useCaseProgress({
  caseId,
  companyId: companyIdProp,
  caseSnapshot,
  missionLink: initialMissionLink,
  onRefresh,
  enabled = true,
  initialTaskId,
  pollWhileActive = true,
}: {
  caseId: string | null;
  companyId?: string | null;
  caseSnapshot?: AgenticCaseProgressCase | null;
  missionLink?: CaseAgenticMissionLink | null;
  onRefresh?: () => void;
  enabled?: boolean;
  /** Hint from ingest flow or batch mission status. */
  initialTaskId?: string | null;
  pollWhileActive?: boolean;
}) {
  const contextCompanyId = useCaseCheckingTenantId();
  const companyId = companyIdProp ?? contextCompanyId;
  const initialHint = applyMissionLinkHint(initialMissionLink, initialTaskId);
  const [missionLink, setMissionLink] = useState<CaseAgenticMissionLink | null>(initialHint.missionLink);
  const [taskId, setTaskId] = useState<string | null>(initialHint.taskId);
  const [hasResolvedActiveTask, setHasResolvedActiveTask] = useState(
    () => !pollWhileActive && Boolean(initialMissionLink),
  );
  const onRefreshRef = useRef(onRefresh);
  const missionLinkRef = useRef(missionLink);
  const wasActiveRef = useRef(false);
  const hasRefreshedRef = useRef(false);
  const skipMountFetch = Boolean(initialMissionLink) && !pollWhileActive;

  onRefreshRef.current = onRefresh;
  missionLinkRef.current = missionLink;

  const displayMode: CaseCheckProgressDisplayMode = caseCheckProgressDisplayMode(missionLink, caseSnapshot);
  const visible = enabled && (shouldShowAgenticProgress(missionLink, caseSnapshot) || shouldShowMissionAttentionState(missionLink));
  const shouldPoll =
    Boolean(missionLink?.is_active) ||
    shouldShowMissionAttentionState(missionLink) ||
    Boolean(missionLink?.chain_failed) ||
    Boolean(missionLink?.intake_failed);

  const refreshMissionLink = useCallback(async () => {
    if (!caseId) return null;
    const next = await getCaseAgenticMission(caseId);
    setMissionLink(next);
    setTaskId((current) => {
      if (shouldPreserveProgressTaskId(current, next)) return current;
      const picked = pickProgressTaskId(null, next, initialTaskId);
      if (picked) return picked;
      if (current && next?.is_active) return current;
      return null;
    });
    setHasResolvedActiveTask(true);
    if (next?.is_active) wasActiveRef.current = true;
    const done = isMissionPollingComplete(next);
    if (wasActiveRef.current && done && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      onRefreshRef.current?.();
    }
    return next;
  }, [caseId, initialTaskId]);

  const refreshActiveTask = useCallback(async () => {
    if (!caseId || !enabled) {
      setTaskId(null);
      setHasResolvedActiveTask(false);
      return null;
    }
    try {
      const active = await getCaseActiveTask(caseId);
      setTaskId(pickProgressTaskId(active.task_id, missionLinkRef.current, initialTaskId));
      return active.task_id;
    } catch {
      setTaskId(pickProgressTaskId(null, missionLinkRef.current, initialTaskId));
      return null;
    } finally {
      setHasResolvedActiveTask(true);
    }
  }, [caseId, enabled, initialTaskId]);

  useEffect(() => {
    if (!initialMissionLink || pollWhileActive) return;
    const hint = applyMissionLinkHint(initialMissionLink, initialTaskId);
    setMissionLink(hint.missionLink);
    setTaskId((current) => {
      if (shouldPreserveProgressTaskId(current, hint.missionLink)) return current;
      return hint.taskId;
    });
    setHasResolvedActiveTask(true);
  }, [initialMissionLink, initialTaskId, pollWhileActive]);

  useEffect(() => {
    if (!enabled || !caseId) {
      setMissionLink(null);
      setTaskId(null);
      setHasResolvedActiveTask(false);
      return;
    }

    if (skipMountFetch) return;

    let cancelled = false;
    setHasResolvedActiveTask(false);

    void (async () => {
      try {
        await refreshMissionLink();
      } catch {
        if (!cancelled) {
          setMissionLink(initialMissionLink ?? null);
          setTaskId(pickProgressTaskId(null, initialMissionLink ?? null, initialTaskId));
          setHasResolvedActiveTask(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [caseId, enabled, initialMissionLink, initialTaskId, refreshMissionLink, skipMountFetch]);

  useCaseCheckingRealtime({
    enabled: enabled && Boolean(caseId) && pollWhileActive,
    companyId,
    caseId,
    onCaseChange: (_changedCaseId, document) => {
      const fromWs = caseCheckingActiveTaskId(document);
      if (isRunnerBackgroundTaskId(fromWs)) {
        setTaskId(fromWs);
      } else if (
        document.metadata &&
        "active_task_id" in document.metadata &&
        document.metadata.active_task_id == null
      ) {
        setTaskId((current) => (isRunnerBackgroundTaskId(current) ? null : current));
      }
      setHasResolvedActiveTask(true);
    },
  });

  useEffect(() => {
    if (!pollWhileActive || !enabled || !caseId || !shouldPoll) return undefined;

    const intervalId = window.setInterval(() => {
      void refreshMissionLink();
    }, ACTIVE_TASK_POLL_MS);

    return () => window.clearInterval(intervalId);
  }, [caseId, enabled, pollWhileActive, refreshMissionLink, shouldPoll]);

  const streamTaskIds = hasResolvedActiveTask && taskId ? [taskId] : [];
  const taskStream = useTaskStream(streamTaskIds, {
    stepTaskIds: streamTaskIds.length > 0 ? streamTaskIds : undefined,
  });

  useEffect(() => {
    if (!taskId || taskStream.isLoadingInitial) return;
    if (!taskStream.isInProgress && taskStream.finishedCount > 0) {
      const link = missionLinkRef.current;
      const keepFailedRunner =
        Boolean(link?.intake_failed || link?.attention_kind === "intake_failed") &&
        taskId === (link?.last_failed_runner_task_id || link?.runner_task_id);
      const missionStillActive = Boolean(link?.is_active) && !isMissionPollingComplete(link);
      if (missionStillActive || keepFailedRunner) {
        void refreshMissionLink();
        return;
      }
      setTaskId(null);
      void refreshMissionLink();
    }
  }, [refreshMissionLink, taskId, taskStream.finishedCount, taskStream.isInProgress, taskStream.isLoadingInitial]);

  const label = useMemo(() => {
    if (!visible) return null;
    if (missionLink && (isAgenticMissionBlocked(missionLink) || missionLink.chain_failed || missionLink.intake_failed)) {
      return agenticProgressLabel(missionLink);
    }
    if (taskId) {
      const detailed = buildCaseProgressLabel({
        steps: taskStream.steps,
        overallProgress: taskStream.overallProgress,
        statusMessage: taskStream.statusMessage,
        missionLink,
        isLoadingInitial: taskStream.isLoadingInitial,
      });
      if (detailed) return detailed;
      if (taskStream.isInProgress) return buildCaseProgressFallback(missionLink, "Processing");
      return null;
    }
    if (missionLink) {
      return agenticProgressLabel(missionLink, {
        preferActiveTaskDisplayName: !taskId && Boolean(missionLink.has_running_work),
      });
    }
    return buildCaseProgressFallback(missionLink);
  }, [
    missionLink,
    taskId,
    taskStream.isInProgress,
    taskStream.isLoadingInitial,
    taskStream.overallProgress,
    taskStream.statusMessage,
    taskStream.steps,
    visible,
  ]);

  const runnerInFlight = Boolean(taskId) && (taskStream.isInProgress || taskStream.isLoadingInitial);
  const agenticRunning =
    Boolean(missionLink?.has_running_work) ||
    ["queued", "working"].includes((missionLink?.agentic_task_status || "").toLowerCase());
  const isProcessing = visible && (runnerInFlight || agenticRunning);

  return {
    missionLink,
    label,
    visible,
    displayMode,
    isProcessing,
    taskId,
    hasResolvedActiveTask,
    refreshMissionLink,
    refreshActiveTask,
    taskStream,
  };
}
